import React from "react";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SEO } from "@/components/common/SEO";
import { Breadcrumbs } from "@/components/common/Breadcrumbs";

const GROUPS = [
  {
    id: "orders",
    title: "Orders",
    items: [
      { q: "How do I place an order?", a: "Simply add your favourite pieces to your bag and proceed to checkout. You'll receive an email confirmation as soon as your order is placed." },
      { q: "Can I modify or cancel my order?", a: "Orders can be modified or cancelled within 2 hours of placement. Contact us at hello@elorajewels.store and we'll be happy to help." },
      { q: "How do I track my order?", a: "Once dispatched, you'll receive a tracking link via email and SMS so you can follow your order every step of the way." },
    ],
  },
  {
    id: "shipping",
    title: "Shipping",
    items: [
      { q: "Do you offer free shipping?", a: "Yes — we offer complimentary insured shipping across India on all orders above ₹25,000." },
      { q: "How long will delivery take?", a: "Orders are dispatched within 24-48 hours and typically arrive within 3-6 business days depending on your location." },
      { q: "Do you ship internationally?", a: "We currently ship across India. International shipping is coming soon — join our newsletter to be the first to know." },
    ],
  },
  {
    id: "returns",
    title: "Returns & Exchanges",
    items: [
      { q: "What is your return policy?", a: "We offer a 15-day easy return and exchange window. Items must be unworn and in their original packaging with tags intact." },
      { q: "Are custom pieces returnable?", a: "Custom, engraved and made-to-order pieces are final sale and cannot be returned or exchanged." },
      { q: "How do refunds work?", a: "Once your return is received and inspected, refunds are processed to your original payment method within 5-7 business days." },
    ],
  },
  {
    id: "care",
    title: "Jewellery Care",
    items: [
      { q: "How should I store my jewellery?", a: "Store each piece separately in a dry, soft pouch away from direct sunlight to prevent scratches and tarnish." },
      { q: "How do I clean my jewellery?", a: "Gently polish with the soft cloth included with your order. Avoid contact with perfume, lotion, and water for lasting shine." },
      { q: "Do you offer repairs?", a: "Yes. Every ÉLORA piece is backed by a lifetime craftsmanship guarantee. Contact us for cleaning and repair services." },
    ],
  },
  {
    id: "payments",
    title: "Payments",
    items: [
      { q: "What payment methods do you accept?", a: "We accept all major credit and debit cards, UPI, net banking and popular wallets through our secure checkout." },
      { q: "Is my payment information secure?", a: "Absolutely. All transactions are encrypted and processed through PCI-compliant payment gateways. We never store your card details." },
      { q: "Do you offer EMI?", a: "Yes, no-cost EMI is available on eligible orders at checkout for select cards and banks." },
    ],
  },
  {
    id: "custom",
    title: "Custom Orders",
    items: [
      { q: "Can I customise a piece?", a: "Certainly. From resizing to bespoke commissions, our artisans can bring your vision to life. Reach out via our contact page to begin." },
      { q: "How long do custom orders take?", a: "Custom pieces typically take 3-4 weeks depending on the complexity of the design. We'll keep you updated throughout." },
    ],
  },
  {
    id: "materials",
    title: "Product Materials",
    items: [
      { q: "What metals do you use?", a: "We use hallmarked 18k and 22k gold, sterling silver and premium gold vermeil, all nickel-free and skin-friendly." },
      { q: "Are your diamonds certified?", a: "Yes, all our diamonds and gemstones are ethically sourced and come with certification of authenticity." },
      { q: "Are your pearls real?", a: "We use genuine AAA-grade freshwater and South Sea pearls, hand-selected for their lustre and quality." },
    ],
  },
];

const POLICIES = [
  { id: "privacy", title: "Privacy Policy", text: "We respect your privacy and are committed to protecting your personal data. Information collected at checkout and sign-up is used solely to process orders, improve your experience and — with your consent — send you updates. We never sell your data to third parties. You may request access to or deletion of your data at any time by contacting hello@elorajewels.store." },
  { id: "terms", title: "Terms & Conditions", text: "By using this website and placing an order, you agree to our terms of service. All product descriptions, prices and availability are subject to change without notice. Images are representative; slight variations may occur as each piece is handcrafted. All content on this site is the property of ÉLORA JEWELS and may not be reproduced without permission." },
];

export default function FaqPage() {
  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
      <SEO title="FAQ & Help" description="Frequently asked questions about ÉLORA orders, shipping, returns, jewellery care, payments and custom pieces." />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "FAQ" }]} />

      <header className="mt-6 text-center">
        <span className="text-luxe text-xs font-medium text-gold">Help Centre</span>
        <h1 className="mt-4 font-serif text-4xl font-light text-ink sm:text-5xl">Frequently Asked Questions</h1>
        <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground">Everything you need to know about shopping with ÉLORA. Can't find an answer? <Link to="/contact" className="text-gold hover:underline">Contact us</Link>.</p>
      </header>

      <div className="mt-12 space-y-12">
        {GROUPS.map((g) => (
          <section key={g.id} id={g.id} className="scroll-mt-28">
            <h2 className="font-serif text-2xl text-ink">{g.title}</h2>
            <Accordion type="single" collapsible className="mt-3">
              {g.items.map((it, i) => (
                <AccordionItem key={it.q} value={`${g.id}-${i}`} className="border-border">
                  <AccordionTrigger className="text-left text-sm text-ink hover:no-underline">{it.q}</AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-muted-foreground">{it.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        ))}

        {POLICIES.map((p) => (
          <section key={p.id} id={p.id} className="scroll-mt-28 border-t border-border pt-10">
            <h2 className="font-serif text-2xl text-ink">{p.title}</h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.text}</p>
          </section>
        ))}
      </div>

      <div className="mt-14 border border-border bg-cream p-8 text-center">
        <p className="font-serif text-2xl text-ink">Still have questions?</p>
        <p className="mt-2 text-sm text-muted-foreground">Our client care team is here to help.</p>
        <Link to="/contact" className="mt-5 inline-block bg-ink px-9 py-4 text-[11px] uppercase tracking-[0.2em] text-ivory hover:bg-gold">
          Contact Us
        </Link>
      </div>
    </div>
  );
}
